import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  computed,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  NgbDateStruct,
  NgbDatepickerModule,
  NgbDropdownModule,
  NgbModal,
  NgbPaginationModule,
  NgbPopover,
  NgbPopoverModule,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCalendar, faClock, faPen, faRotateLeft, faSortDown, faSortUp, faTimes } from '@fortawesome/free-solid-svg-icons';
import { AlreadyClonedProject, AlreadyClonedWarningModalComponent } from '../cloned-warning-modal/cloned-warning-modal.component';

export type AgreementType = 'SINGLE' | 'MULTI' | string;
export type InvoiceType = 'percentage' | 'dollar' | string;


export type AutomationJobStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'
  | 'PAUSED'
  | string;

export interface AutomationJobRow {
  id?: string;
  jobId?: string;
  scheduleId?: string;
  projectId?: string;
  projectName?: string;
  clientId?: string;
  clientName?: string;
  status?: AutomationJobStatus;
  scheduledFor?: Date | string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  errorMessage?: string | null;
  attempts?: number;
  maxAttempts?: number;
  [key: string]: any;
}

export interface MultiYearOption {
  title?: string | null;
  acronym?: string | null;
  isEnabled?: boolean;
  numOfYears?: number | null;
}

export type MultiYearOptionsMap = Record<string, MultiYearOption>;

export interface BatchCloneRow {
  clientId: string;
  projectId: string;
  myaId?: string;

  clientName?: string;
  projectName: string;
  agreementType?: AgreementType;

  newProjectName?: string;
  newProjectYear?: number;

  isAutoSendProposal: boolean;
  proposalSendDateAndTime?: Date | string;

  isAutoAccept: boolean;
  isAutoInvoice: boolean;

  invoiceAmount?: number;
  invoiceType?: InvoiceType;
  invoiceIssueDate?: Date | string;
  invoiceDueDate?: Date | string;

  isAutoSendInvoice?: boolean;
  invoiceSendDate?: Date | string;

  lastAcceptedProposal?: string

  isCloned?: boolean;
  year?: number;

  // NEW
  multiOptions?: MultiYearOptionsMap | null;
  selectedMultiOptionKey?: string | null;
  selectedMultiOption?: MultiYearOption | null;
  selectedMultiOptionKeys?: string[];
  selectedMultiOptions?: MultiYearOptionsMap | null;
  autoSelect?: string | null;
}

type SortDir = 'ASC' | 'DESC';
type SortKey = keyof BatchCloneRow;

type BooleanKeys =
  | 'isAutoSendProposal'
  | 'isAutoAccept'
  | 'isAutoInvoice'
  | 'isAutoSendInvoice';

type BulkKey =
  | 'newProjectName'
  | 'isAutoSendProposal'
  | 'isAutoAccept'
  | 'proposalSendDateAndTime'
  | 'proposalSendTime'
  | 'isAutoInvoice'
  | 'invoiceAmount'
  | 'invoiceType'
  | 'invoiceIssueDate'
  | 'invoiceDueDate'
  | 'isAutoSendInvoice'
  | 'invoiceSendDate'
  | 'invoiceSendTime'
  | 'multiOptionKey'
  | 'multiOptionKeys'
  | 'autoSelect';

type DateOnlyKeys =
  | 'invoiceIssueDate'
  | 'invoiceDueDate';

type DateTimeKeys =
  | 'proposalSendDateAndTime'
  | 'invoiceSendDate';

type DateFieldKeys = DateOnlyKeys | DateTimeKeys;

@Component({
  selector: 'app-batch-cloning-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    NgbDatepickerModule,
    NgbDropdownModule,
    NgbPaginationModule,
    NgbTooltipModule,
    NgbPopoverModule
  ],
  styleUrls: ['./batch-cloning-table.component.css'],
  templateUrl: './batch-cloning-table.component.html',
})
export class BatchCloningTableComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {
  @Input({ required: true }) rows: BatchCloneRow[] = [];
  @Input() isWizardMode = false;
  @Input() preselectedProjectIds: string[] = [];
  @Input() projectType: string = '';

  @Output() clone = new EventEmitter<BatchCloneRow[]>();
  @Output() selectionChange = new EventEmitter<BatchCloneRow[]>();
  @Output() validationChange = new EventEmitter<boolean>();
  @Output() yearChange = new EventEmitter<number | null>();

  faRotateLeft = faRotateLeft;
  faSortDown = faSortDown;
  faSortUp = faSortUp;
  faPen = faPen;
  faCalendar = faCalendar;
  faClock = faClock;
  faTimes = faTimes;

  today: NgbDateStruct = (() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
  })();

  isMobile = signal<boolean>(window.innerWidth < 768);
  searchValue = signal<string>('');
  page = signal<number>(1);
  limit = signal<number>(20);
  sortBy = signal<SortKey>('projectName');
  sortDirection = signal<SortDir>('ASC');
  rowsSignal = signal<BatchCloneRow[]>([]);
  private readonly defaultSelectedYear = new Date().getFullYear() - 1;
  selectedYear = signal<number | null>(this.defaultSelectedYear);

  years: number[] = [];
  selectedProjectIds = signal<Set<string>>(new Set());

  bulk = signal<Record<BulkKey, any>>({
    newProjectName: '',
    isAutoSendProposal: false,
    isAutoAccept: false,
    proposalSendDateAndTime: null,
    proposalSendTime: '09:00',
    isAutoInvoice: false,
    invoiceAmount: '',
    invoiceType: 'percentage',
    invoiceIssueDate: null,
    invoiceDueDate: null,
    isAutoSendInvoice: false,
    invoiceSendDate: null,
    invoiceSendTime: '09:00',
    multiOptionKey: '',
    multiOptionKeys: [],
    autoSelect: '',
  });

  @ViewChild('tableWrap', { static: false }) tableWrap?: ElementRef<HTMLElement>;
  @ViewChild('xBar', { static: false }) xBar?: ElementRef<HTMLElement>;
  @ViewChildren(NgbPopover) private readonly ngbPopovers!: QueryList<NgbPopover>;

  xBarWidth = signal<number>(0);
  private syncingScroll = false;
  private readonly rowDateDrafts = new Map<string, Partial<Record<DateFieldKeys, Date>>>();

  private readonly enabledOptionEntriesCache = new Map<
    string,
    { signature: string; entries: Array<[string, MultiYearOption]> }
  >();
  private readonly selectedOptionKeysCache = new Map<
    string,
    { signature: string; keys: string[] }
  >();
  private readonly selectedOptionEntriesCache = new Map<
    string,
    { signature: string; entries: Array<[string, MultiYearOption]> }
  >();

  private static readonly dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  private static readonly dateOnlyFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  private readonly onWindowResize = (): void => {
    this.isMobile.set(window.innerWidth < 768);
    this.recalcXBar();
  };

  constructor(
    private ngbModal: NgbModal
  ) {
    window.addEventListener('resize', this.onWindowResize);
  }

  ngOnInit(): void {
    const currentYear = new Date().getFullYear();

    this.years = Array.from(
      { length: currentYear - 2022 + 1 },
      (_, i) => currentYear - i
    );

    this.selectedYear.set(this.defaultSelectedYear);
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.recalcXBar());
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onWindowResize);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preselectedProjectIds']) {
      const incoming = this.preselectedProjectIds ?? [];
      const current = this.selectedProjectIds();

      const isSameSelection =
        incoming.length === current.size &&
        incoming.every((id) => current.has(id));

      if (!isSameSelection) {
        this.selectedProjectIds.set(new Set(incoming));
      }
    }

    if (changes['rows']) {
      this.clearMultiOptionCaches();
      let rows = this.mergeIncomingRows(this.rows ?? [])
      this.rowsSignal.set(rows);
      this.page.set(1);
    }

    if (changes['selectedYear']) {
      this.page.set(1);
    }

  }

  private clearMultiOptionCaches(): void {
    this.enabledOptionEntriesCache.clear();
    this.selectedOptionKeysCache.clear();
    this.selectedOptionEntriesCache.clear();
  }

  trackByProjectId(_index: number, row: BatchCloneRow): string {
    return row.projectId;
  }

  trackByOptionEntry(_index: number, entry: [string, MultiYearOption]): string {
    return entry[0];
  }

  trackByOptionChoice(_index: number, choice: { key: string; label: string }): string {
    return choice.key;
  }

  onYearChange(year: number | null): void {
    this.selectedYear.set(year);

    this.selectedProjectIds.set(new Set());
    this.page.set(1);
    this.resetBulkState();
    this.yearChange.emit(year);
  }

  clearYear(event: MouseEvent): void {
    event.stopPropagation();

    this.selectedYear.set(null);

    this.selectedProjectIds.set(new Set());
    this.emitSelectionChange();
    this.page.set(1);

    this.yearChange.emit(null);
  }

  private resetBulkState(): void {
    this.bulk.set({
      newProjectName: '',
      isAutoSendProposal: false,
      isAutoAccept: false,
      proposalSendDateAndTime: null,
      proposalSendTime: '09:00',
      isAutoInvoice: false,
      invoiceAmount: '',
      invoiceType: 'percentage',
      invoiceIssueDate: null,
      invoiceDueDate: null,
      isAutoSendInvoice: false,
      invoiceSendDate: null,
      invoiceSendTime: '09:00',
      multiOptionKey: '',
      multiOptionKeys: [],
      autoSelect: '',
    });
  }


  private mergeIncomingRows(incoming: BatchCloneRow[]): BatchCloneRow[] {
    const existingByProjectId = new Map(
      this.rowsSignal().map((row) => [row.projectId, row])
    );

    const editableKeys: Array<keyof BatchCloneRow> = [
      'newProjectName',
      'newProjectYear',
      'isAutoSendProposal',
      'proposalSendDateAndTime',
      'isAutoAccept',
      'isAutoInvoice',
      'invoiceAmount',
      'invoiceType',
      'invoiceIssueDate',
      'invoiceDueDate',
      'isAutoSendInvoice',
      'invoiceSendDate',
      'selectedMultiOptionKey',
      'selectedMultiOption',
      'selectedMultiOptionKeys',
      'selectedMultiOptions',
      'autoSelect',
    ];

    return incoming.map((row) => {
      const existing = existingByProjectId.get(row.projectId);

      const normalized: BatchCloneRow = {
        ...row,

        // defaults for all project types
        isAutoSendProposal: !!row.isAutoSendProposal,
        isAutoAccept: !!row.isAutoAccept,
        isAutoInvoice: !!row.isAutoInvoice,
        isAutoSendInvoice: !!row.isAutoSendInvoice,
        invoiceType: (row.invoiceType ?? 'percentage') as InvoiceType,
      };

      // expiring-only defaults
      if (this.isExpiringStep()) {
        normalized.selectedMultiOptionKeys = this.normalizeSelectedMultiOptionKeys(row);
        normalized.selectedMultiOptions = this.buildSelectedMultiOptionsMap(
          normalized,
          normalized.selectedMultiOptionKeys
        );
        normalized.autoSelect = row.autoSelect ?? null;

        this.syncExpiringSelectionState(normalized);
      }

      if (!existing) return normalized;

      for (const key of editableKeys) {
        const existingValue = (existing as any)[key];

        if (existingValue !== undefined) {
          (normalized as any)[key] = existingValue;
        }
      }

      if (this.isExpiringStep()) {
        this.syncExpiringSelectionState(normalized);
      }

      return normalized;
    });
  }

  private emitSelectionChange(): void {
    if (!this.isWizardMode) return;

    const selected = this.selectedRowsForClone();

    this.selectionChange.emit(selected);

    const isValid =
      selected.length > 0 &&
      selected.every((row) => this.isRowValid(row));

    this.validationChange.emit(isValid);
  }

  private recalcXBar(): void {
    const wrap = this.tableWrap?.nativeElement;
    const bar = this.xBar?.nativeElement;
    if (!wrap || !bar) return;

    this.xBarWidth.set(wrap.scrollWidth);
    bar.scrollLeft = wrap.scrollLeft;
  }

  /** Close popovers (e.g. date/time in `container="body"`) so they do not float after the grid moves. */
  closeOpenPopoversOnTableScroll(): void {
    this.ngbPopovers?.forEach((popover) => {
      if (popover.isOpen()) {
        popover.close();
      }
    });
  }

  onTableScroll(): void {
    this.closeOpenPopoversOnTableScroll();
    if (this.syncingScroll) return;
    const wrap = this.tableWrap?.nativeElement;
    const bar = this.xBar?.nativeElement;
    if (!wrap || !bar) return;

    this.syncingScroll = true;
    bar.scrollLeft = wrap.scrollLeft;
    this.syncingScroll = false;
  }

  onXBarScroll(): void {
    this.closeOpenPopoversOnTableScroll();
    if (this.syncingScroll) return;
    const wrap = this.tableWrap?.nativeElement;
    const bar = this.xBar?.nativeElement;
    if (!wrap || !bar) return;

    this.syncingScroll = true;
    wrap.scrollLeft = bar.scrollLeft;
    this.syncingScroll = false;
  }

  private processedRows = computed(() => {
    let data = [...this.rowsSignal()];

    const q = this.searchValue().trim().toLowerCase();
    if (q) {
      data = data.filter((r) =>
        `${r.clientName} ${r.projectName} ${r.clientId} ${r.projectId} ${r.newProjectName ?? ''}`
          .toLowerCase()
          .includes(q)
      );
    }

    const key = this.sortBy();
    const dir = this.sortDirection() === 'ASC' ? 1 : -1;

    data.sort((a, b) => {
      const av = (a as any)[key];
      const bv = (b as any)[key];

      const aNorm = this.sortValue(av);
      const bNorm = this.sortValue(bv);

      if (aNorm > bNorm) return dir;
      if (aNorm < bNorm) return -dir;
      return 0;
    });

    return data;
  });

  totalCount = computed(() => this.processedRows().length);

  viewRows = computed(() => {
    const start = (this.page() - 1) * this.limit();
    return this.processedRows().slice(start, start + this.limit());
  });

  allVisibleSelected = computed(() => {
    const rows = this.processedRows();
    if (!rows.length) return false;
    const sel = this.selectedProjectIds();
    return rows.every((r) => sel.has(r.projectId));
  });

  someVisibleSelected = computed(() => {
    const rows = this.processedRows();
    if (!rows.length) return false;
    const sel = this.selectedProjectIds();
    const selectedCount = rows.reduce((acc, r) => acc + (sel.has(r.projectId) ? 1 : 0), 0);
    return selectedCount > 0 && selectedCount < rows.length;
  });

  selectedRowsForClone = computed(() => {
    const sel = this.selectedProjectIds();
    return this.rowsSignal().filter((r) => sel.has(r.projectId));
  });

  search(val: string): void {
    this.searchValue.set(val ?? '');
    this.page.set(1);
    queueMicrotask(() => this.recalcXBar());
  }

  sort(column: SortKey): void {
    if (this.sortBy() === column) {
      this.sortDirection.set(this.sortDirection() === 'ASC' ? 'DESC' : 'ASC');
    } else {
      this.sortBy.set(column);
      this.sortDirection.set('ASC');
    }
  }

  isSortedBy(column: SortKey): boolean {
    return this.sortBy() === column;
  }

  isSortedASC(): boolean {
    return this.sortDirection() === 'ASC';
  }

  setLimit(n: number): void {
    this.limit.set(n);
    this.page.set(1);
    queueMicrotask(() => this.recalcXBar());
  }

  setCurrentPage(p: number): void {
    this.page.set(p);
    queueMicrotask(() => this.recalcXBar());
  }

  resetTableConfiguration(): void {
    this.searchValue.set('');
    this.page.set(1);
    this.limit.set(20);
    this.sortBy.set('projectName');
    this.sortDirection.set('ASC');
    this.selectedYear.set(this.defaultSelectedYear);
    this.selectedProjectIds.set(new Set());
    this.emitSelectionChange();
    queueMicrotask(() => this.recalcXBar());
  }

  isNotDefault(): boolean {
    return (
      this.searchValue().trim().length > 0 ||
      this.page() !== 1 ||
      this.limit() !== 20 ||
      this.sortBy() !== 'projectName' ||
      this.sortDirection() !== 'ASC' ||
      this.selectedYear() !== this.defaultSelectedYear
    );
  }

  isRowSelected(row: BatchCloneRow): boolean {
    return this.selectedProjectIds().has(row.projectId);
  }

  toggleRowSelected(row: BatchCloneRow, checked: boolean): void {
    const next = new Set(this.selectedProjectIds());

    if (checked) {
      next.add(row.projectId);

      const current = (row.newProjectName ?? '').trim();
      if (!current) {
        row.newProjectName = this.buildNextProjectName(row.projectName, row.year);
      }

      if (this.isExpiringStep() && this.getSelectedMultiOptionKeys(row).length === 0) {
        const firstEnabled = this.getEnabledMultiOptionEntries(row)[0];
        if (firstEnabled) {
          this.applyRowMultiOption(row, firstEnabled[0]);
        }
      }
    } else {
      next.delete(row.projectId);

      if (this.isExpiringStep()) {
        this.clearRowMultiOptions(row);
      }
    }

    this.selectedProjectIds.set(next);
    this.emitSelectionChange();
  }

  toggleSelectAllVisible(checked: boolean): void {
    const next = new Set(this.selectedProjectIds());

    for (const r of this.processedRows()) {
      if (checked) {
        next.add(r.projectId);

        const current = (r.newProjectName ?? '').trim();
        if (!current) {
          r.newProjectName = this.buildNextProjectName(r.projectName, r.year);
        }

        if (this.isExpiringStep() && this.getSelectedMultiOptionKeys(r).length === 0) {
          const firstEnabled = this.getEnabledMultiOptionEntries(r)[0];
          if (firstEnabled) {
            this.applyRowMultiOption(r, firstEnabled[0]);
          }
        }
      } else {
        next.delete(r.projectId);

        if (this.isExpiringStep()) {
          this.clearRowMultiOptions(r);
        }
      }
    }

    this.selectedProjectIds.set(next);
    this.emitSelectionChange();
  }


  toggleBoolean(row: BatchCloneRow, key: BooleanKeys, checked: boolean): void {
    if (key === 'isAutoInvoice' && checked && !this.canCreateInvoice(row)) {
      return;
    }

    (row as any)[key] = !!checked;

    if (key === 'isAutoAccept' && checked) {
      row.isAutoSendProposal = false;
    }

    if (key === 'isAutoAccept' && !checked) {
      row.isAutoInvoice = false;
      row.isAutoSendInvoice = false;
      row.invoiceAmount = undefined;
      row.invoiceType = 'percentage';
      row.invoiceIssueDate = undefined;
      row.invoiceDueDate = undefined;
      row.invoiceSendDate = undefined;
    }

    if (key === 'isAutoInvoice' && checked) {
      row.invoiceType = 'percentage';
      row.invoiceAmount = undefined;
    }

    if (key === 'isAutoInvoice' && !checked) {
      row.isAutoSendInvoice = false;
      row.invoiceType = 'percentage';
      row.invoiceAmount = undefined;
      row.invoiceIssueDate = undefined;
      row.invoiceDueDate = undefined;
      row.invoiceSendDate = undefined;
    }

    this.emitSelectionChange();
  }

  updateText(row: BatchCloneRow, key: 'newProjectName', val: string): void {
    (row as any)[key] = val ?? '';
    this.emitSelectionChange();
  }

  updateNumber(row: BatchCloneRow, key: 'invoiceAmount', val: string): void {
    const n = val === '' ? undefined : Number(val);
    (row as any)[key] = Number.isFinite(n as any) ? n : undefined;
    this.emitSelectionChange();
  }

  updateInvoiceType(row: BatchCloneRow, val: string): void {
    (row as any).invoiceType = (val ?? '').trim();
    this.emitSelectionChange();
  }

  setDateTime(row: BatchCloneRow, key: 'proposalSendDateAndTime' | 'invoiceSendDate', val: string): void {
    (row as any)[key] = val ? new Date(val) : undefined;
    this.emitSelectionChange();
  }

  setDate(row: BatchCloneRow, key: 'invoiceIssueDate' | 'invoiceDueDate', val: string): void {
    if (!val) {
      (row as any)[key] = undefined;
      this.emitSelectionChange();
      return;
    }
    const [y, m, d] = val.split('-').map(Number);
    (row as any)[key] = new Date(y, (m ?? 1) - 1, d ?? 1);
    this.emitSelectionChange();
  }

  setDateOnly(row: BatchCloneRow, key: DateOnlyKeys, val: string): void {
    if (!val) {
      (row as any)[key] = undefined;
      this.emitSelectionChange();
      return;
    }

    const [y, m, d] = val.split('-').map(Number);
    (row as any)[key] = new Date(y, (m ?? 1) - 1, d ?? 1);
    this.emitSelectionChange();
  }

  bulkSetDateTime(
    key: DateTimeKeys,
    value: NgbDateStruct | null,
    timeStr: string = '00:00',
  ): void {
    if (!value) {
      this.applyToFilteredRows((row) => {
        (row as any)[key] = undefined;
      });

      this.emitSelectionChange();
      return;
    }

    const date = this.createUtcDateTime(value, timeStr);

    this.applyToFilteredRows((row) => {
      (row as any)[key] = new Date(date);
    });

    this.emitSelectionChange();
  }


  private getRowDraftKey(row: BatchCloneRow): string {
    return row.projectId;
  }

  private getDraftMapForRow(row: BatchCloneRow): Partial<Record<DateFieldKeys, Date>> {
    return {
      ...(this.rowDateDrafts.get(this.getRowDraftKey(row)) ?? {}),
    };
  }

  getRowDateDraft(
    row: BatchCloneRow,
    key: DateFieldKeys,
  ): Date | string | undefined {
    const draft = this.rowDateDrafts.get(
      this.getRowDraftKey(row),
    );

    return draft?.[key] ?? (row as any)[key];
  }

  getProposalSendDateDraftValue(row: BatchCloneRow): Date | string | undefined {
    return this.getRowDateDraft(row, 'proposalSendDateAndTime');
  }

  getInvoiceIssueDateDraftValue(row: BatchCloneRow): Date | string | undefined {
    return this.getRowDateDraft(row, 'invoiceIssueDate');
  }

  getInvoiceDueDateDraftValue(row: BatchCloneRow): Date | string | undefined {
    return this.getRowDateDraft(row, 'invoiceDueDate');
  }

  getInvoiceSendDateDraftValue(row: BatchCloneRow): Date | string | undefined {
    return this.getRowDateDraft(row, 'invoiceSendDate');
  }

  setRowDateDraft(
    row: BatchCloneRow,
    key: DateFieldKeys,
    value: NgbDateStruct | null,
  ): void {
    const draft = this.getDraftMapForRow(row);

    if (!value) {
      delete draft[key];
      this.rowDateDrafts.set(this.getRowDraftKey(row), draft);
      return;
    }

    const existingValue = this.getRowDateDraft(row, key);
    const existingDate = existingValue
      ? new Date(existingValue)
      : null;

    const hasValidExistingDate =
      existingDate !== null &&
      !Number.isNaN(existingDate.getTime());

    const hours = this.isInvoiceDateOnlyKey(key)
      ? 0
      : hasValidExistingDate
        ? existingDate.getUTCHours()
        : 0;

    const minutes = this.isInvoiceDateOnlyKey(key)
      ? 0
      : hasValidExistingDate
        ? existingDate.getUTCMinutes()
        : 0;

    draft[key] = new Date(
      Date.UTC(
        value.year,
        value.month - 1,
        value.day,
        hours,
        minutes,
        0,
        0,
      ),
    );

    this.rowDateDrafts.set(
      this.getRowDraftKey(row),
      draft,
    );
  }

  setRowTimeDraft(
    row: BatchCloneRow,
    key: DateTimeKeys,
    timeStr: string,
  ): void {
    const existingValue = this.getRowDateDraft(row, key);

    if (!existingValue) {
      return;
    }

    const date = new Date(existingValue);

    if (Number.isNaN(date.getTime())) {
      return;
    }

    const [hoursValue = '0', minutesValue = '0'] =
      (timeStr || '00:00').split(':');

    const hours = Number(hoursValue);
    const minutes = Number(minutesValue);

    date.setUTCHours(
      Number.isFinite(hours) ? hours : 0,
      Number.isFinite(minutes) ? minutes : 0,
      0,
      0,
    );

    const draft = this.getDraftMapForRow(row);
    draft[key] = date;

    this.rowDateDrafts.set(
      this.getRowDraftKey(row),
      draft,
    );
  }

  confirmRowDateDraft(
    row: BatchCloneRow,
    key: DateFieldKeys,
  ): void {
    const rowKey = this.getRowDraftKey(row);
    const draft = this.rowDateDrafts.get(rowKey);
    const value = draft?.[key];

    if (value) {
      (row as any)[key] = new Date(value);
    }

    if (draft) {
      delete draft[key];
      this.rowDateDrafts.set(rowKey, draft);
    }

    this.emitSelectionChange();
  }

  discardRowDateDraft(
    row: BatchCloneRow,
    key: DateFieldKeys,
  ): void {
    const rowKey = this.getRowDraftKey(row);
    const draft = this.rowDateDrafts.get(rowKey);
    if (!draft) return;

    delete draft[key];
    this.rowDateDrafts.set(rowKey, draft);
  }

  toNgbDate(value?: Date | string): NgbDateStruct | null {
    if (!value) {
      return null;
    }

    const date = value instanceof Date
      ? value
      : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    };
  }


  setBulkDateValue(
    key: 'proposalSendDateAndTime' | 'invoiceIssueDate' | 'invoiceDueDate' | 'invoiceSendDate',
    val: NgbDateStruct | null
  ): void {
    this.bulk.update((current) => ({
      ...current,
      [key]: val,
    }));
  }

  clearRowDate(row: BatchCloneRow, key: DateTimeKeys): void {
    const rowKey = this.getRowDraftKey(row);
    const draft = this.rowDateDrafts.get(rowKey);

    if (draft) {
      delete draft[key];
      this.rowDateDrafts.set(rowKey, draft);
    }

    (row as any)[key] = undefined;
    this.emitSelectionChange();
  }

  setDateFromNgb(row: BatchCloneRow, key: 'invoiceIssueDate' | 'invoiceDueDate', val: NgbDateStruct | null): void {
    if (!val) {
      (row as any)[key] = undefined;
    } else {
      (row as any)[key] = new Date(val.year, val.month - 1, val.day);
    }
    this.emitSelectionChange();
  }

  setDateOnlyFromNgb(row: BatchCloneRow, key: DateOnlyKeys, val: NgbDateStruct | null): void {
    if (!val) {
      (row as any)[key] = undefined;
    } else {
      (row as any)[key] = new Date(val.year, val.month - 1, val.day);
    }
    this.emitSelectionChange();
  }

  setTime(
    row: BatchCloneRow,
    key: DateTimeKeys,
    timeStr: string,
  ): void {
    const existingValue = (row as any)[key];

    if (!existingValue) {
      return;
    }

    const date = new Date(existingValue);

    if (Number.isNaN(date.getTime())) {
      return;
    }

    const [hoursValue = '0', minutesValue = '0'] =
      (timeStr || '00:00').split(':');

    date.setUTCHours(
      Number(hoursValue) || 0,
      Number(minutesValue) || 0,
      0,
      0,
    );

    (row as any)[key] = date;

    this.emitSelectionChange();
  }

  toTimeInputValue(value?: Date | string): string {
    if (!value) {
      return '';
    }

    const date = value instanceof Date
      ? value
      : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
  }

  formatDateTimeSummary(value?: Date | string): string {
    if (!value) {
      return '';
    }

    const date = value instanceof Date
      ? value
      : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return BatchCloningTableComponent.dateTimeFormatter.format(date);
  }

  formatDateOnlySummary(d?: Date | string): string {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    if (isNaN(dt.getTime())) return '';
    return BatchCloningTableComponent.dateOnlyFormatter.format(dt);
  }

  toDateTimeLocalValue(d?: Date): string {
    if (!d) return '';
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    const hh = String(dt.getHours()).padStart(2, '0');
    const mm = String(dt.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${hh}:${mm}`;
  }

  private applyToFilteredRows(mutator: (row: BatchCloneRow) => void): void {
    for (const r of this.processedRows()) mutator(r);
  }

  bulkSetText(key: 'newProjectName', value: string): void {
    this.applyToFilteredRows((r) => ((r as any)[key] = value ?? ''));
    this.emitSelectionChange();
  }

  bulkSetBoolean(key: BooleanKeys, value: boolean): void {
    this.applyToFilteredRows((r) => {
      if (key === 'isAutoInvoice' && value && !this.canCreateInvoice(r)) {
        return;
      }

      (r as any)[key] = !!value;

      if (key === 'isAutoAccept' && value) {
        r.isAutoSendProposal = false;
      }

      if (key === 'isAutoAccept' && !value) {
        r.isAutoInvoice = false;
        r.isAutoSendInvoice = false;
        r.invoiceAmount = undefined;
        r.invoiceType = 'percentage';
        r.invoiceIssueDate = undefined;
        r.invoiceDueDate = undefined;
        r.invoiceSendDate = undefined;
      }

      if (key === 'isAutoInvoice' && value) {
        r.invoiceType = 'percentage';
        r.invoiceAmount = undefined;
      }

      if (key === 'isAutoInvoice' && !value) {
        r.isAutoSendInvoice = false;
        r.invoiceType = 'percentage';
        r.invoiceAmount = undefined;
        r.invoiceIssueDate = undefined;
        r.invoiceDueDate = undefined;
        r.invoiceSendDate = undefined;
      }
    });

    this.emitSelectionChange();
  }

  bulkSetNumber(key: 'invoiceAmount', value: string): void {
    const n = value === '' ? undefined : Number(value);
    this.applyToFilteredRows((r) => ((r as any)[key] = Number.isFinite(n as any) ? n : undefined));
    this.emitSelectionChange();
  }

  bulkSetInvoiceType(value: string): void {
    const v = (value ?? '').trim();
    this.applyToFilteredRows((r) => ((r as any).invoiceType = v || 'percentage'));
    this.emitSelectionChange();
  }

  bulkSetDate(key: 'invoiceIssueDate' | 'invoiceDueDate', value: NgbDateStruct | null): void {
    if (!value) {
      this.applyToFilteredRows((r) => ((r as any)[key] = undefined));
      this.emitSelectionChange();
      return;
    }

    const dt = new Date(value.year, value.month - 1, value.day);
    this.applyToFilteredRows((r) => ((r as any)[key] = new Date(dt)));
    this.emitSelectionChange();
  }

  formatDateTime(d?: Date): string {
    if (!d) return '';
    try {
      return new Date(d).toLocaleString();
    } catch {
      return '';
    }
  }

  formatMoney(n?: number): string {
    if (n === null || n === undefined) return '';
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  private sortValue(v: any): any {
    if (v === null || v === undefined) return '';
    if (v instanceof Date) return v.getTime();
    if (typeof v === 'boolean') return v ? 1 : 0;
    if (typeof v === 'number') return v;
    return String(v).toLowerCase();
  }

  batchCloneSelected(): void {
    if (this.isWizardMode) {
      this.emitSelectionChange();
      return;
    }

    const selected = this.selectedRowsForClone();

    if (selected.some((row) => !this.isRowValid(row))) {
      return;
    }

    const alreadyCloned: AlreadyClonedProject[] = selected
      .filter((r) => !!r.isCloned)
      .map((r) => ({
        projectId: r.projectId,
        projectName: r.projectName,
        clientName: r.clientName,
      }));

    if (alreadyCloned.length === 0) {
      this.proceedBatchClone(selected);
      return;
    }

    const ref = this.ngbModal.open(AlreadyClonedWarningModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });

    ref.componentInstance.projects = alreadyCloned;

    ref.result
      .then((confirmed) => {
        if (confirmed) this.proceedBatchClone(selected);
      })
      .catch(() => { });
  }

  private buildNextProjectName(projectName: string, baseYear?: number): string {
    const name = (projectName ?? '').toString().trim();
    if (!name) return '';

    const re = /\b(19\d{2}|20\d{2})\b/g;
    const matches = [...name.matchAll(re)];
    if (matches.length) {
      if (Number.isFinite(Number(baseYear))) {
        const by = Number(baseYear);
        const idxExact = matches.findIndex((m) => Number(m[0]) === by);

        if (idxExact >= 0) {
          const m = matches[idxExact];
          const found = Number(m[0]);
          const idx = m.index ?? -1;
          if (idx >= 0) {
            return (
              name.slice(0, idx) +
              String(found + 1) +
              name.slice(idx + m[0].length)
            );
          }
        }
      }

      const last = matches[matches.length - 1];
      const found = Number(last[0]);
      const idx = last.index ?? -1;

      if (idx >= 0) {
        return (
          name.slice(0, idx) +
          String(found + 1) +
          name.slice(idx + last[0].length)
        );
      }
    }

    const by = Number(baseYear);
    if (Number.isFinite(by)) {
      return `${name} ${by + 1}`.replace(/\s+/g, ' ').trim();
    }


    return name;
  }

  private proceedBatchClone(selected: BatchCloneRow[]): void {
    const validSelected = selected.filter((r) => this.isRowValid(r));

    const payload = validSelected.map((r) => ({
      clientId: r.clientId,
      projectId: r.projectId,
      projectName: r.projectName,
      newProjectName: r.newProjectName,
      isAutoSendProposal: r.isAutoSendProposal ?? false,
      isAutoAccept: this.isExpiringStep() ? !!r.autoSelect : (r.isAutoAccept ?? false),
      autoSelect: this.isExpiringStep() ? (r.autoSelect ?? null) : null,
      selectedMultiOptionKeys: this.isExpiringStep() ? this.getSelectedMultiOptionKeys(r) : [],
      selectedMultiOptions: this.isExpiringStep() ? (r.selectedMultiOptions ?? null) : null,
      selectedMultiOptionKey: this.isExpiringStep() ? (r.selectedMultiOptionKey ?? null) : null,
      selectedMultiOption: this.isExpiringStep() ? (r.selectedMultiOption ?? null) : null,
      isAutoInvoice: r.isAutoInvoice ?? false,
      isAutoSendInvoice: r.isAutoSendInvoice ?? false,
      proposalSendDateAndTime: r.proposalSendDateAndTime,
      invoiceAmount: r.invoiceAmount,
      invoiceType: (r.invoiceType ?? '').toString().trim() || 'percentage',
      invoiceIssueDate: r.invoiceIssueDate,
      invoiceDueDate: r.invoiceDueDate,
      invoiceSendDate: r.invoiceSendDate,
    }));

    this.clone.emit(payload);
    this.resetTableConfiguration();
  }

  private isValidDateValue(value?: Date | string): boolean {
    if (!value) return false;
    const dt = value instanceof Date ? value : new Date(value);
    return !Number.isNaN(dt.getTime());
  }

  private toDateOnlyComparable(value?: Date | string): number | null {
    if (!this.isValidDateValue(value)) return null;

    const dt = value instanceof Date ? value : new Date(value);
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
  }

  isProposalSendDateInvalid(row: BatchCloneRow): boolean {
    return !!row.isAutoSendProposal && !this.isValidDateValue(row.proposalSendDateAndTime);
  }

  isInvoiceDependencyInvalid(row: BatchCloneRow): boolean {
    return !!row.isAutoInvoice && !this.canCreateInvoice(row);
  }

  isInvoiceTypeInvalid(row: BatchCloneRow): boolean {
    if (!row.isAutoInvoice) return false;
    return row.invoiceType !== 'percentage' && row.invoiceType !== 'dollar';
  }

  isInvoiceAmountInvalid(row: BatchCloneRow): boolean {
    if (!row.isAutoInvoice) return false;
    return row.invoiceAmount === undefined || row.invoiceAmount === null || Number(row.invoiceAmount) < 0;
  }

  isInvoiceIssueDateInvalid(row: BatchCloneRow): boolean {
    if (!row.isAutoInvoice) return false;
    return !this.isValidDateValue(row.invoiceIssueDate);
  }

  isInvoiceDueDateInvalid(row: BatchCloneRow): boolean {
    if (!row.isAutoInvoice) return false;
    return !this.isValidDateValue(row.invoiceDueDate);
  }

  isInvoiceDateOrderInvalid(row: BatchCloneRow): boolean {
    if (!row.isAutoInvoice) return false;

    const issue = this.toDateOnlyComparable(row.invoiceIssueDate);
    const due = this.toDateOnlyComparable(row.invoiceDueDate);

    if (issue === null || due === null) return false;
    return due < issue;
  }

  getRowValidationErrors(row: BatchCloneRow): string[] {
    const errors: string[] = [];

    if (this.isProposalSendDateInvalid(row)) {
      errors.push('Send proposal requires a valid send date.');
    }

    if (this.isInvoiceDependencyInvalid(row)) {
      errors.push('Create invoice requires Accept proposal to be enabled.');
    }

    if (this.isInvoiceTypeInvalid(row)) {
      errors.push('Invoice type must be Percentage or Dollar.');
    }

    if (this.isInvoiceAmountInvalid(row)) {
      errors.push('Invoice amount must be 0 or greater.');
    }

    if (this.isInvoiceIssueDateInvalid(row)) {
      errors.push('Issue date is required and must be valid.');
    }

    if (this.isInvoiceDueDateInvalid(row)) {
      errors.push('Due date is required and must be valid.');
    }

    if (this.isInvoiceDateOrderInvalid(row)) {
      errors.push('Due date must be on or after issue date.');
    }

    if (this.isMultiYearSelectionInvalid(row)) {
      errors.push('Multi year requires at least one selected option.');
    }

    return errors;
  }

  isRowValid(row: BatchCloneRow): boolean {
    return this.getRowValidationErrors(row).length === 0;
  }

  hasSelectedRowsWithErrors(): boolean {
    return this.selectedRowsForClone().some((row) => !this.isRowValid(row));
  }

  hasEnabledMultiOption(row: BatchCloneRow): boolean {
    return this.getEnabledMultiOptionEntries(row).length > 0;
  }

  canCreateInvoice(row: BatchCloneRow): boolean {
    return this.isExpiringStep() ? !!row.autoSelect : !!row.isAutoAccept;
  }

  getCreateInvoiceDisabledTooltip(row: BatchCloneRow): string | null {
    if (this.canCreateInvoice(row)) return null;

    return this.isExpiringStep()
      ? 'Please select Will auto accept first'
      : 'Please enable accept proposal first';
  }

  isMultiYearSelectionInvalid(row: BatchCloneRow): boolean {
    if (!this.isExpiringStep()) return false;
    if (!this.selectedProjectIds().has(row.projectId)) return false;
    return this.getSelectedMultiOptionKeys(row).length === 0;
  }

  isAutoSelectInvalid(row: BatchCloneRow): boolean {
    if (!this.isExpiringStep()) return false;

    const selectedKeys = this.getSelectedMultiOptionKeys(row);

    // Empty autoSelect is allowed
    if (!row.autoSelect) return false;

    // If provided, it must be one of the selected multi-year options
    return !selectedKeys.includes(row.autoSelect);
  }

  toggleRowMultiOption(row: BatchCloneRow, optionKey: string, checked: boolean): void {
    const next = new Set(this.selectedProjectIds());
    const option = row.multiOptions?.[optionKey];

    if (!option?.isEnabled) return;

    const selectedKeys = new Set(this.getSelectedMultiOptionKeys(row));

    if (checked) {
      selectedKeys.add(optionKey);
      next.add(row.projectId);

      const current = (row.newProjectName ?? '').trim();
      if (!current) {
        row.newProjectName = this.buildNextProjectName(row.projectName, row.year);
      }
    } else {
      selectedKeys.delete(optionKey);
    }

    row.selectedMultiOptionKeys = Array.from(selectedKeys);
    row.selectedMultiOptions = this.buildSelectedMultiOptionsMap(row, row.selectedMultiOptionKeys);

    if (row.selectedMultiOptionKeys.length === 0) {
      this.clearRowMultiOptions(row);
      next.delete(row.projectId);
    } else {
      // Keep autoSelect only if it is still selected.
      // Otherwise clear it instead of defaulting to the first selected option.
      if (row.autoSelect && !row.selectedMultiOptionKeys.includes(row.autoSelect)) {
        row.autoSelect = null;
      }

      this.syncExpiringSelectionState(row);
    }

    this.selectedProjectIds.set(next);
    this.emitSelectionChange();
  }

  private applyDefaultEnabledMultiOption(row: BatchCloneRow): void {
    const [entry] = this.getEnabledMultiOptionEntries(row);
    if (!entry) return;

    this.applyRowMultiOption(row, entry[0]);
  }

  toggleAllEnabledMultiOptions(checked: boolean): void {
    const next = new Set(this.selectedProjectIds());

    for (const row of this.processedRows()) {
      if (!this.hasEnabledMultiOption(row)) continue;

      if (checked) {
        const keys = this.getEnabledMultiOptionEntries(row).map(([key]) => key);
        row.selectedMultiOptionKeys = keys;
        row.selectedMultiOptions = this.buildSelectedMultiOptionsMap(row, keys);
        if (row.autoSelect && !keys.includes(row.autoSelect)) {
          row.autoSelect = null;
        }
        this.syncExpiringSelectionState(row);
        next.add(row.projectId);

        const current = (row.newProjectName ?? '').trim();
        if (!current) {
          row.newProjectName = this.buildNextProjectName(row.projectName, row.year);
        }
      } else {
        this.clearRowMultiOptions(row);
        next.delete(row.projectId);
      }
    }

    this.selectedProjectIds.set(next);
    this.emitSelectionChange();
  }

  allVisibleEnabledMultiOptionsSelected = computed(() => {
    if (!this.isExpiringStep()) return false;

    const rows = this.processedRows().filter((row) => this.hasEnabledMultiOption(row));
    if (!rows.length) return false;

    return rows.every((row) => {
      const enabledKeys = this.getEnabledMultiOptionEntries(row).map(([key]) => key);
      const selectedKeys = this.getSelectedMultiOptionKeys(row);

      return (
        enabledKeys.length > 0 &&
        enabledKeys.every((key) => selectedKeys.includes(key)) &&
        this.selectedProjectIds().has(row.projectId)
      );
    });
  });

  someVisibleEnabledMultiOptionsSelected = computed(() => {
    if (!this.isExpiringStep()) return false;

    const rows = this.processedRows().filter((row) => this.hasEnabledMultiOption(row));
    if (!rows.length) return false;

    const selectedCount = rows.filter(
      (row) =>
        this.getSelectedMultiOptionKeys(row).length > 0 &&
        this.selectedProjectIds().has(row.projectId)
    ).length;

    return selectedCount > 0 && selectedCount < rows.length;
  });

  bulkSetMultiOption(optionKeys: string[] | string): void {
    const keys = Array.isArray(optionKeys) ? optionKeys : [optionKeys].filter(Boolean);
    const next = new Set(this.selectedProjectIds());

    this.applyToFilteredRows((row) => {
      const enabledKeys = keys.filter((key) => !!row.multiOptions?.[key]?.isEnabled);
      if (!enabledKeys.length) return;

      const selectedKeys = Array.from(new Set([...this.getSelectedMultiOptionKeys(row), ...enabledKeys]));
      row.selectedMultiOptionKeys = selectedKeys;
      row.selectedMultiOptions = this.buildSelectedMultiOptionsMap(row, selectedKeys);
      if (row.autoSelect && !selectedKeys.includes(row.autoSelect)) {
        row.autoSelect = null;
      }
      this.syncExpiringSelectionState(row);

      next.add(row.projectId);

      const current = (row.newProjectName ?? '').trim();
      if (!current) {
        row.newProjectName = this.buildNextProjectName(row.projectName, row.year);
      }
    });

    this.selectedProjectIds.set(next);
    this.emitSelectionChange();
  }

  clearBulkMultiOption(): void {
    this.bulk.update((value) => ({
      ...value,
      multiOptionKey: '',
      multiOptionKeys: [],
    }));
  }

  toggleBulkMultiOptionKey(optionKey: string, checked: boolean): void {
    this.bulk.update((value) => {
      const current = new Set<string>(value.multiOptionKeys ?? []);
      if (checked) {
        current.add(optionKey);
      } else {
        current.delete(optionKey);
      }

      return {
        ...value,
        multiOptionKeys: Array.from(current),
      };
    });
  }

  isBulkMultiOptionKeySelected(optionKey: string): boolean {
    return (this.bulk().multiOptionKeys ?? []).includes(optionKey);
  }

  setBulkAutoSelect(optionKey: string): void {
    this.bulk.update((value) => ({
      ...value,
      autoSelect: optionKey,
    }));
  }

  clearBulkAutoSelect(): void {
    this.bulk.update((value) => ({
      ...value,
      autoSelect: '',
    }));
  }

  bulkSetAutoSelect(optionKey: string): void {
    if (!optionKey) return;

    this.applyToFilteredRows((row) => {
      const selectedKeys = this.getSelectedMultiOptionKeys(row);
      if (!selectedKeys.includes(optionKey)) return;

      row.autoSelect = optionKey;
      this.syncExpiringSelectionState(row);
    });

    this.emitSelectionChange();
  }

  isExpiringStep(): boolean {
    return this.projectType === 'expiring';
  }

  getEnabledMultiOptionEntries(row: BatchCloneRow): Array<[string, MultiYearOption]> {
    const options = row.multiOptions;
    const keys: string[] = [];

    if (options) {
      for (const key of Object.keys(options)) {
        if (options[key]?.isEnabled) keys.push(key);
      }
    }

    const signature = keys.join('|');
    const cached = this.enabledOptionEntriesCache.get(row.projectId);

    if (cached && cached.signature === signature) {
      return cached.entries;
    }

    const entries = keys.map(
      (key) => [key, options![key]] as [string, MultiYearOption]
    );

    this.enabledOptionEntriesCache.set(row.projectId, { signature, entries });

    return entries;
  }

  getSelectedMultiOptionKeys(row: BatchCloneRow): string[] {
    const options = row.multiOptions;
    const keys: string[] = [];

    const addKey = (key?: string | null) => {
      if (!key) return;
      if (!options?.[key]?.isEnabled) return;
      if (keys.includes(key)) return;
      keys.push(key);
    };

    if (Array.isArray(row.selectedMultiOptionKeys)) {
      for (const key of row.selectedMultiOptionKeys) addKey(key);
    }

    addKey(row.selectedMultiOptionKey);

    const signature = keys.join('|');
    const cached = this.selectedOptionKeysCache.get(row.projectId);

    if (cached && cached.signature === signature) {
      return cached.keys;
    }

    this.selectedOptionKeysCache.set(row.projectId, { signature, keys });

    return keys;
  }

  getSelectedMultiOptionEntries(row: BatchCloneRow): Array<[string, MultiYearOption]> {
    const keys = this.getSelectedMultiOptionKeys(row);
    const signature = keys.join('|');
    const cached = this.selectedOptionEntriesCache.get(row.projectId);

    if (cached && cached.signature === signature) {
      return cached.entries;
    }

    const entries = keys.map(
      (key) => [key, row.multiOptions![key]] as [string, MultiYearOption]
    );

    this.selectedOptionEntriesCache.set(row.projectId, { signature, entries });

    return entries;
  }

  isRowMultiOptionSelected(row: BatchCloneRow, optionKey: string): boolean {
    return this.getSelectedMultiOptionKeys(row).includes(optionKey);
  }

  getMultiOptionLabel(optionKey: string, option: MultiYearOption): string {
    return (
      option?.title?.trim() ||
      option?.acronym?.trim() ||
      (option?.numOfYears ? `${option.numOfYears} Year Agreement` : optionKey)
    );
  }

  private bulkMultiOptionChoices = computed<Array<{ key: string; label: string }>>(() => {
    if (!this.isExpiringStep()) return [];

    const deduped = new Map<string, { key: string; label: string }>();

    for (const row of this.processedRows()) {
      for (const [key, option] of this.getEnabledMultiOptionEntries(row)) {
        const label = this.getMultiOptionLabel(key, option);
        const dedupeKey = `${key}__${label}`;

        if (!deduped.has(dedupeKey)) {
          deduped.set(dedupeKey, { key, label });
        }
      }
    }

    return Array.from(deduped.values());
  });

  getBulkMultiOptionChoices(): Array<{ key: string; label: string }> {
    return this.bulkMultiOptionChoices();
  }

  setBulkMultiOptionKey(optionKey: string): void {
    this.toggleBulkMultiOptionKey(optionKey, true);
  }

  setRowAutoSelect(row: BatchCloneRow, optionKey: string | null): void {
    if (!optionKey) {
      row.autoSelect = null;
      this.syncExpiringSelectionState(row);
      this.emitSelectionChange();
      return;
    }

    if (!this.getSelectedMultiOptionKeys(row).includes(optionKey)) return;

    row.autoSelect = optionKey;
    this.syncExpiringSelectionState(row);
    this.emitSelectionChange();
  }

  applyRowMultiOption(row: BatchCloneRow, optionKey: string | null): void {
    if (!optionKey) {
      this.clearRowMultiOptions(row);
      return;
    }

    const option = row.multiOptions?.[optionKey];
    if (!option?.isEnabled) return;

    const selectedKeys = new Set(this.getSelectedMultiOptionKeys(row));
    selectedKeys.add(optionKey);

    row.selectedMultiOptionKeys = Array.from(selectedKeys);
    row.selectedMultiOptions = this.buildSelectedMultiOptionsMap(
      row,
      row.selectedMultiOptionKeys
    );

    // Do not default autoSelect
    if (row.autoSelect && !row.selectedMultiOptionKeys.includes(row.autoSelect)) {
      row.autoSelect = null;
    }

    this.syncExpiringSelectionState(row);
  }

  private normalizeSelectedMultiOptionKeys(row: BatchCloneRow): string[] {
    const fromArray = Array.isArray(row.selectedMultiOptionKeys)
      ? row.selectedMultiOptionKeys
      : [];
    const fromLegacy = row.selectedMultiOptionKey ? [row.selectedMultiOptionKey] : [];

    return Array.from(new Set([...fromArray, ...fromLegacy].filter(Boolean)));
  }

  private buildSelectedMultiOptionsMap(
    row: BatchCloneRow,
    keys: string[]
  ): MultiYearOptionsMap | null {
    const options = row.multiOptions ?? null;
    if (!options || Object.keys(options).length === 0) return null;

    const selectedKeys = new Set(keys);

    const mapped = Object.entries(options).reduce<MultiYearOptionsMap>(
      (acc, [key, option]) => {
        acc[key] = {
          title: option?.title ?? null,
          acronym: option?.acronym ?? null,

          // true if selected, false if not selected
          isEnabled: selectedKeys.has(key),

          numOfYears: option?.numOfYears ?? null,
        };

        return acc;
      },
      {}
    );

    return mapped;
  }

  private syncExpiringSelectionState(row: BatchCloneRow): void {
    const selectedKeys = this.normalizeSelectedMultiOptionKeys(row).filter(
      (key) => !!row.multiOptions?.[key]?.isEnabled
    );

    row.selectedMultiOptionKeys = selectedKeys;
    row.selectedMultiOptions = this.buildSelectedMultiOptionsMap(row, selectedKeys);

    // Empty autoSelect is allowed.
    // Only clear it if it no longer belongs to the selected multi-year options.
    if (row.autoSelect && !selectedKeys.includes(row.autoSelect)) {
      row.autoSelect = null;
    }

    // Legacy single selected option should follow multi-year selection,
    // not the Will auto accept field.
    const legacyKey = selectedKeys[0] ?? null;

    row.selectedMultiOptionKey = legacyKey;
    row.selectedMultiOption = legacyKey
      ? this.buildSelectedMultiOptionsMap(row, [legacyKey])?.[legacyKey] ?? null
      : null;

    row.isAutoAccept = this.isExpiringStep() ? !!row.autoSelect : row.isAutoAccept;
  }

  private clearRowMultiOptions(row: BatchCloneRow): void {
    row.selectedMultiOptionKey = null;
    row.selectedMultiOption = null;
    row.selectedMultiOptionKeys = [];
    row.selectedMultiOptions = null;
    row.autoSelect = null;

    if (this.isExpiringStep()) {
      row.isAutoAccept = false;
      row.isAutoInvoice = false;
      row.isAutoSendInvoice = false;
      row.invoiceAmount = undefined;
      row.invoiceType = 'percentage';
      row.invoiceIssueDate = undefined;
      row.invoiceDueDate = undefined;
      row.invoiceSendDate = undefined;
    }
  }

  private isInvoiceDateOnlyKey(key: DateTimeKeys | DateOnlyKeys): boolean {
    return key === 'invoiceIssueDate' || key === 'invoiceDueDate';
  }

  private createUtcDateTime(
    date: NgbDateStruct,
    timeStr: string = '00:00',
  ): Date {
    const [hoursValue = '0', minutesValue = '0'] = timeStr.split(':');

    const hours = Number(hoursValue);
    const minutes = Number(minutesValue);

    return new Date(
      Date.UTC(
        date.year,
        date.month - 1,
        date.day,
        Number.isFinite(hours) ? hours : 0,
        Number.isFinite(minutes) ? minutes : 0,
        0,
        0,
      ),
    );
  }

}